// Frontend mirror of the backend COMPONENT_TIER registry
// (app/services/token_limit_service.py). Keep these in sync — the backend is the
// real enforcement; this is for hiding/showing UI and choosing run-vs-upsell.
//
// `effective_tier` (from authAPI.whoami) already folds a live free trial up to
// pro_tier, so gate on that, not the raw stored tier.
//
// There is no free tier. `lapsed_tier` is what an account becomes when its
// trial ends or a plan stops — an exit state, never one you sign up into. It
// ranks 0 because it grants nothing, which is what free_tier used to mean.

export const TIER_RANK = {
  lapsed_tier: 0,
  pro_tier: 1,
  ultimate_tier: 2,
  ultimate_plus_tier: 3,
  admin: 99,
};

const PRO = "pro_tier";

// Minimum tier per AI-coach component. Unknown components default to PRO
// (fail closed) so a new gated surface isn't accidentally treated as free.
export const COMPONENT_TIER = {
  chat: PRO,
  plan_generate: PRO,
  plan_adapt: PRO,
  plan_propose: PRO,
  plan_confirm: PRO,
  plan_resolve: PRO,
  plan_edit_review: PRO,
  stretch_buddy: PRO,
  recovery_insights: PRO,
  activity_notification: PRO,
  home_brief: PRO,
  workout_generate: PRO,
  coaching: PRO,
  coach_codes: "ultimate_tier",
};

export function componentRequiredTier(component) {
  return COMPONENT_TIER[component] ?? PRO;
}

// Does `effectiveTier` meet the component's minimum?
export function componentAllowed(effectiveTier, component) {
  const have = TIER_RANK[effectiveTier] ?? 0;
  const need = TIER_RANK[componentRequiredTier(component)] ?? 1;
  return have >= need;
}

export function isProOrBetter(effectiveTier) {
  return (TIER_RANK[effectiveTier] ?? 0) >= TIER_RANK[PRO];
}

/**
 * The tier the app may act on, or null while it isn't known. whoami's answer
 * (this session) is the truth; before it answers, or while it can't be
 * reached, the tier stored from an earlier visit stands in only when it grants
 * Pro or better: a stored non-granting tier may be stale (a trial or upgrade
 * since) and must not show the lapsed copy. Persona QA 2026-10-25.
 */
export function resolveEffectiveTier(user, fresh) {
  if (!user) return null;
  const tier = user.effective_tier || user.tier;
  if (fresh) return tier || "lapsed_tier";
  return tier && isProOrBetter(tier) ? tier : null;
}

/**
 * What screens read. `allowed` stays fail-closed (an unknown tier starts no
 * gated action); `denied` / `isLapsed` are only true for a KNOWN tier, so an
 * unknown one never shows "Subscribe to keep your coach".
 */
export function entitlementFrom(tier) {
  const known = tier !== null && tier !== undefined;
  const effectiveTier = known ? tier : "lapsed_tier";
  return {
    effectiveTier,
    known,
    isPro: isProOrBetter(effectiveTier),
    isLapsed: known && !isProOrBetter(effectiveTier),
    allowed: (component) => componentAllowed(effectiveTier, component),
    denied: (component) => known && !componentAllowed(effectiveTier, component),
  };
}
